import { useState } from "react";
import type { CaseRecord } from "../../types";
import { displayFactTitle, formatFactDate } from "../../lib/factMeta";
import {
  COMMITMENT_STATE_LABELS,
  type CommitmentRow as CommitmentRowData,
} from "../../lib/commitments";
import type { PartyUi } from "../../lib/parties";
import FactProvenance from "./FactProvenance";
import { AiMark, DateMark } from "./ProvenanceMark";

// Capped preview inside the (already collapsed) section — long commitment
// lists show the first few rows + "view all N" (Round 13, Phase 1).
const ROW_PREVIEW = 8;

// Commitments and deadlines — a vigilance tool, not a judgment engine.
// States are neutral: PENDING (deadline ahead), DATE PASSED (deadline
// elapsed — says nothing about whether it was met), OPEN (no deadline),
// RESOLVED (user-marked, persisted by content key so it survives
// re-extraction). Rows dedupe on party + normalized text + deadline.
//
// Round 10: resolutions state is OWNED BY THE PARENT (StructuredOverview)
// so the main table and the party panel share one source of truth —
// resolving in either view updates both. The row renderer is exported and
// shared (one implementation).

export function CommitmentRowView({
  row,
  recordById,
  onNavigateToRecord,
  onToggleResolved,
  partyUi,
  showParty = true,
}: {
  row: CommitmentRowData;
  recordById: Map<string, CaseRecord>;
  onNavigateToRecord?: (recordId: string) => void;
  onToggleResolved: (key: string, resolved: boolean) => void;
  partyUi?: PartyUi;
  showParty?: boolean;
}) {
  const { fact: f, alsoIn, key, state } = row;
  const rec = recordById.get(f.record_id);
  const resolved = state === "resolved";
  return (
    <tr
      className={resolved ? "commit-resolved-row" : undefined}
      style={resolved ? { opacity: 0.55 } : undefined}
    >
      <td>
        {formatFactDate(f.date)}{" "}
        <DateMark factDate={f.date} recordDate={rec?.record_date} />
      </td>
      {showParty && (
        <td>
          {f.party ? (
            partyUi?.clickable(f.party) ? (
              <button
                type="button"
                className="fact-party-link"
                onClick={() => partyUi.open(f.party!)}
                title="View party details"
              >
                {partyUi.display(f.party)}
              </button>
            ) : (
              <span>{partyUi?.display(f.party) ?? f.party}</span>
            )
          ) : (
            "—"
          )}{" "}
          {f.party && <AiMark what="Party attribution" />}
        </td>
      )}
      <td>
        <button
          type="button"
          className="fact-source-link"
          onClick={() => onNavigateToRecord?.(f.record_id)}
          title={rec?.subject ?? ""}
        >
          {displayFactTitle(f)}
        </button>{" "}
        <AiMark what="Commitment title" />
        <FactProvenance
          recordIds={[f.record_id, ...alsoIn]}
          recordById={recordById}
          onNavigateToRecord={onNavigateToRecord}
        />
      </td>
      <td>{f.deadline ? formatFactDate(f.deadline) : "—"}</td>
      <td>
        <span className={`commit-status commit-${state}`}>
          {COMMITMENT_STATE_LABELS[state]}
        </span>
      </td>
      <td>
        <button
          type="button"
          className="fact-source-link"
          onClick={() => onToggleResolved(key, !resolved)}
          title={resolved ? "Move back to not done" : "Mark this done"}
        >
          {resolved ? "Mark not done" : "Mark done"}
        </button>
      </td>
    </tr>
  );
}

export default function CommitmentsTable({
  rows,
  recordById,
  onToggleResolved,
  onNavigateToRecord,
  partyUi,
}: {
  // Rows are computed by the parent (one source of truth shared with the
  // collapsed summary row and the party panel) — Round 13.
  rows: CommitmentRowData[];
  recordById: Map<string, CaseRecord>;
  onToggleResolved: (key: string, resolved: boolean) => void;
  onNavigateToRecord?: (recordId: string) => void;
  partyUi?: PartyUi;
}) {
  const [showAll, setShowAll] = useState(false);
  if (rows.length === 0) return null;

  const shown = showAll ? rows : rows.slice(0, ROW_PREVIEW);

  return (
    <>
      <div className="table-scroll">
      <table className="fact-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Party</th>
            <th>Commitment</th>
            <th>Deadline</th>
            <th>Status</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {shown.map((row) => (
            <CommitmentRowView
              key={row.fact.id}
              row={row}
              recordById={recordById}
              onNavigateToRecord={onNavigateToRecord}
              onToggleResolved={onToggleResolved}
              partyUi={partyUi}
            />
          ))}
        </tbody>
      </table>
      </div>
      {rows.length > ROW_PREVIEW && (
        <button
          type="button"
          className="overview-view-all"
          onClick={() => setShowAll((v) => !v)}
        >
          {showAll ? "▴ Show fewer" : `▾ View all ${rows.length} commitments`}
        </button>
      )}
    </>
  );
}
